import { useContext, useEffect, useState } from "react";
import { BreadTransaction, P, Predicate } from "breadcommon";
import { TransactionColumn } from "./TransactionColumn";
import { TransactionsNavContext } from "./TransactionsScreen";
import { buildClasses } from "../utils/buildClasses";
import { RyeNumericalInput } from "../rye/RyeNumericalInput";
import { TransactionColumnFilterProps } from "./DefaultTransactionColumnHeader";

export function TransactionColumnFilterAmount({
  isOpen,
  onClose,
  hasActiveFilter,
}: TransactionColumnFilterProps): JSX.Element | null {
  const [transactionsNav, setTransactionsNav] = useContext(
    TransactionsNavContext,
  );
  const [minAmount, setMinAmount] = useState<number | null>(null);
  const [maxAmount, setMaxAmount] = useState<number | null>(null);
  const [minAmountFormula, setMinAmountFormula] = useState<string>("");
  const [maxAmountFormula, setMaxAmountFormula] = useState<string>("");

  useEffect(() => {
    if (!hasActiveFilter) {
      setMinAmount(null);
      setMaxAmount(null);
      setMinAmountFormula("");
      setMaxAmountFormula("");
    }
  }, [hasActiveFilter]);

  function createAmountRangePredicate(
    min: number | null,
    max: number | null,
  ): Predicate | null {
    const amountField = P.Field<BreadTransaction, "amount">("amount");
    const predicates: Predicate[] = [];
    if (min !== null) {
      predicates.push(P.GreaterThanEqualTo(amountField, P.Literal(min)));
    }
    if (max !== null) {
      predicates.push(P.LessThanEqualTo(amountField, P.Literal(max)));
    }

    if (predicates.length === 0) {
      return null;
    }
    return P.All(...predicates);
  }

  function applyFilter(min: number | null, max: number | null) {
    const currentSettings =
      transactionsNav.columnSettings[TransactionColumn.AMOUNT];
    setTransactionsNav((prevState) => ({
      ...prevState,
      columnSettings: {
        ...transactionsNav.columnSettings,
        [TransactionColumn.AMOUNT]: {
          ...currentSettings,
          predicate: createAmountRangePredicate(min, max),
        },
      },
    }));
  }

  function handleMinAmountChange(newMinAmount: number | null) {
    setMinAmount(newMinAmount);
    applyFilter(newMinAmount, maxAmount);
  }

  function handleMaxAmountChange(newMaxAmount: number | null) {
    setMaxAmount(newMaxAmount);
    applyFilter(minAmount, newMaxAmount);
  }

  function handleEnter() {
    onClose();
  }

  if (!isOpen) {
    return null;
  }

  return (
    // <div className={buildClasses("flex", "items-center", "gap-2", "p-3")}>
    <div className={buildClasses("flex", "gap-2", "bg-transparent")}>
      <div className={buildClasses("shadow-md", "rounded-md", "bg-surface")}>
        <RyeNumericalInput
          onFormulaChange={setMinAmountFormula}
          onChange={handleMinAmountChange}
          startingText={minAmountFormula}
          size="md"
          width="sm"
          textAlign="left"
          placeholderText="Min"
          onEnter={handleEnter}
          onEsc={onClose}
        />
      </div>
      <div className={buildClasses("shadow-md", "rounded-md", "bg-surface")}>
        <RyeNumericalInput
          onFormulaChange={setMaxAmountFormula}
          onChange={handleMaxAmountChange}
          startingText={maxAmountFormula}
          size="md"
          width="sm"
          textAlign="left"
          placeholderText="Max"
          onEnter={handleEnter}
          onEsc={onClose}
        />
      </div>
    </div>
  );
}
